import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import styles from './LegalPages.module.css';

function PrivacyPolicy() {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Privacy Policy for Sapienta VC website and services."
        canonical="https://sapienta.vc/privacy-policy"
      />
      <div className={styles.container}>
      <p className={styles.effectiveDate}>Last Updated: 07/11/2025</p>
      
      <p className={styles.text}>
        At Sapienta, we are committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information across all of our ventures, websites, and services (collectively referred to as "Services"). By using our Services, you agree to the collection and use of information in accordance with this Privacy Policy.
      </p>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>1. Information We Collect</h2>
        <p className={styles.text}>
          We collect various types of personal information to provide and improve our Services. The types of personal data we may collect include:
        </p>
        <ul className={styles.list}>
          <li className={styles.listItem}>
            <strong>Personal Identification Information:</strong> This may include your name, email address, phone number, billing address, or other personal identifiers.
          </li>
          <li className={styles.listItem}>
            <strong>Usage Data:</strong> This includes information about how you access and use our Services, such as your IP address, browser type, device information, pages viewed, and the time spent on certain pages.
          </li>
          <li className={styles.listItem}>
            <strong>Cookies and Tracking Data:</strong> We may use cookies, web beacons, and similar technologies to track your activities on our website and personalize your experience.
          </li>
          <li className={styles.listItem}>
            <strong>Payment Information:</strong> If you make a purchase or engage in a financial transaction, we may collect payment card details or other financial data, which are processed securely through a third-party payment processor.
          </li>
        </ul>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>2. How We Use Your Information</h2>
        <p className={styles.text}>
          We use the collected information for various purposes, including but not limited to:
        </p>
        <ul className={styles.list}>
          <li className={styles.listItem}>
            <strong>Providing and improving Services:</strong> To deliver the products, services, or content you request and enhance the overall user experience.
          </li>
          <li className={styles.listItem}>
            <strong>Personalizing your experience:</strong> To tailor the content and features we offer based on your preferences and past interactions.
          </li>
          <li className={styles.listItem}>
            <strong>Communicating with you:</strong> To respond to inquiries, provide customer support, send updates, or inform you about new products or services.
          </li>
          <li className={styles.listItem}>
            <strong>Marketing and Advertising:</strong> We may use your data to send promotional materials or targeted advertising, with your consent where required.
          </li>
          <li className={styles.listItem}>
            <strong>Compliance and Legal Obligations:</strong> To comply with legal, regulatory, or contractual obligations, or to protect our legal rights.
          </li>
        </ul>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>3. How We Share Your Information</h2>
        <p className={styles.text}>
          We do not sell, rent, or lease your personal data to third parties. However, we may share your information in the following circumstances:
        </p>
        <ul className={styles.list}>
          <li className={styles.listItem}>
            <strong>Affiliates and Subsidiaries:</strong> As a holding company, we may share information between our ventures or subsidiaries to improve the efficiency of services and communications.
          </li>
          <li className={styles.listItem}>
            <strong>Service Providers:</strong> We may share your data with third-party vendors who assist us in providing services such as payment processing, email delivery, hosting, analytics, and customer support.
          </li>
          <li className={styles.listItem}>
            <strong>Legal Compliance:</strong> We may disclose your information if required by law, regulation, legal process, or government request.
          </li>
          <li className={styles.listItem}>
            <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your personal data may be transferred as part of the transaction.
          </li>
        </ul>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>4. Data Retention</h2>
        <p className={styles.text}>
          We retain your personal data only as long as necessary to fulfill the purposes outlined in this Privacy Policy or as required by law. If you no longer wish to use our Services or would like us to delete your data, please contact us at contact@sapienta.vc, and we will honor your request as applicable by law.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>5. Your Data Protection Rights</h2>
        <p className={styles.text}>
          Depending on your location and the applicable data protection laws (e.g., GDPR, CCPA), you may have certain rights regarding your personal data, including:
        </p>
        <ul className={styles.list}>
          <li className={styles.listItem}>Access: You can request access to the personal data we hold about you.</li>
          <li className={styles.listItem}>Rectification: You may request corrections to any inaccurate or incomplete data.</li>
          <li className={styles.listItem}>Deletion: You may request the deletion of your personal data, subject to certain legal exceptions.</li>
          <li className={styles.listItem}>Objection: You may object to the processing of your data in certain circumstances.</li>
          <li className={styles.listItem}>Portability: You may request a copy of your personal data in a machine-readable format.</li>
          <li className={styles.listItem}>Withdraw Consent: If we rely on your consent for processing data, you can withdraw that consent at any time.</li>
        </ul>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>6. Data Security</h2>
        <p className={styles.text}>
          We take the security of your personal information seriously and implement appropriate technical and organizational measures to protect your data from unauthorized access, alteration, disclosure, or destruction. However, please be aware that no method of data transmission over the Internet or method of electronic storage is completely secure, and we cannot guarantee absolute security.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>7. Cookies and Tracking Technologies</h2>
        <p className={styles.text}>
          We use cookies, web beacons, and similar tracking technologies to enhance your experience on our websites and to analyze how our services are used. These technologies allow us to remember your preferences and settings, analyze traffic patterns and user behavior, and serve personalized content and advertisements.
        </p>
        <p className={styles.text}>
          You can control your cookie preferences through your browser settings or by using our cookie management tool. For more information about cookies, please see our <Link to="/cookie-policy" className={styles.link}>Cookie Policy</Link>.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>8. Third-Party Links</h2>
        <p className={styles.text}>
          Our Services may contain links to third-party websites or services that are not operated by us. We are not responsible for the privacy practices or content of those external sites. We encourage you to review the privacy policies of any third-party websites before providing them with your personal information.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>9. Children's Privacy</h2>
        <p className={styles.text}>
          Our Services are not intended for children under the age of 13 (or under the age of 16 in the European Union). We do not knowingly collect or solicit personal data from children. If we become aware that we have collected personal data from a child under the age of 13, we will take steps to delete such information.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>10. Changes to This Privacy Policy</h2>
        <p className={styles.text}>
          We may update our Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.
        </p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.subHeading}>11. Contact Us</h2>
        <p className={styles.text}>
          If you have any questions or concerns about this Privacy Policy or how we handle your personal data, please contact us at:
        </p>
        <p className={styles.text}>
          <strong>Sapienta Group of Ventures</strong><br />
          Email: legal@sapienta.vc<br />
          Address: 3811 Ditmars Blvd<br />
          Astoria, NY 11105-1803, US
        </p>
      </div>
    </div>
    </>
  );
}

export default PrivacyPolicy; 